import { cloneElement, PropsWithChildren, useEffect, useState } from "react";
import {
    Field,
    TagPicker,
    TagPickerList,
    TagPickerInput,
    TagPickerControl,
    TagPickerProps,
    TagPickerOption,
    TagPickerGroup,
    makeStyles,
    Tag,
    Avatar,
    useTagPickerFilter,
    mergeClasses
} from "@fluentui/react-components";
import {
    InfoRegular
} from "@fluentui/react-icons";
import { useL10n } from "context/l10n-context";
import { ILocaleStrings } from "loc/locale.interfaces";
import { IMultiObjectPickerProps } from "./multi-object-picker.props";
import { getError } from "lib/utils";
import { usePPA } from "context/ppa-context";

type Option = {
    objectId: string;
    displayName: string;
    upn?: string;
    memberType: number;
};

const layoutStyles = makeStyles({
    tagTruncatedPrimaryText: {
        whiteSpace: "nowrap",
        overflowX: "hidden",
        textOverflow: "ellipsis",
    },
    tagPrimaryTextFixedWidth: {
        width: "50px",
    },
    optionContent: {
        whiteSpace: "nowrap",
        overflowX: "hidden",
        textOverflow: "ellipsis",
    },
    optionSecondaryContent: {
        whiteSpace: "nowrap",
    },
});

export default function MultiObjectPicker<T>(props: IMultiObjectPickerProps<T>) {
    const styles = layoutStyles();
    const { t } = useL10n<ILocaleStrings>();
    const [query, setQuery] = useState<string>("");
    const { getApiClient } = usePPA();

    const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
    const [results, setResults] = useState<Option[]>([]);

    const onOptionSelect: TagPickerProps["onOptionSelect"] = (e, data) => {
        if (data.value === "no-options") {
            return;
        }

        setSelectedOptions(
            data.selectedOptions.map(
                (objectId, idx) => {
                    const existing = selectedOptions.filter((o => o.objectId === objectId))[0];
                    if (existing) {
                        return existing;
                    }
                    else {
                        return results.find((o) => o.objectId === objectId)!
                    }
                }
            )
        );

        //
        setQuery("");

    };

    useEffect(() => {
        props.onChange(selectedOptions);
    }, [selectedOptions]);

    const getSearchUrl = (search: string): string => {
        let principalTypes = ``;
        let userType = ``;

        if (props.principalTypes) {
            principalTypes = `&principalTypes=${props.principalTypes.join(`,`)}`;
        }
        if (props.userType) {
            userType = `&userType=${props.userType}`;
        }
        return `/api/principals/search?search=${search}${principalTypes}${userType}`;
    }

    useEffect(() => {
        console.log(`useEffect -> query ->`, query);
        //
        const searchObjects = async (search: string) => {
            try {
                const response = await getApiClient().get<Array<any>>(getSearchUrl(search));
                setResults(response.data ? response.data : []);
            }
            catch (ex: unknown) {
                const error = getError(ex);
                console.log(`ObjectPickerInput -> searchObjects -> Error ->`, error);
            }
        }

        if (query.length > 0) {
            searchObjects(query);
        }
        else {
            setResults([]);
        }
    }, [query]);

    return (
        <div >
            <Field label={t(props.label)} >
                <TagPicker
                    onOptionSelect={onOptionSelect}
                    selectedOptions={selectedOptions.map((option) => option.objectId)}>
                    <TagPickerControl>
                        <TagPickerGroup aria-label="xxxLabel2">
                            {selectedOptions.map((option) => (
                                <Tag
                                    key={option.objectId}
                                    shape="rounded"
                                    media={
                                        <Avatar aria-hidden name={option.displayName} color="colorful" />
                                    }
                                    value={option.objectId}
                                    title={option.displayName}
                                    primaryText={{
                                        className: styles.tagTruncatedPrimaryText
                                    }}>
                                    {option.displayName}
                                </Tag>
                            ))}
                        </TagPickerGroup>
                        <TagPickerInput
                            aria-label={t("pickerFilterPlaceholder")}
                            placeholder={t("pickerFilterPlaceholder")}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)} />
                    </TagPickerControl>
                    <TagPickerList>
                        {results.length > 0 ? (
                            results.map((option) => (
                                <TagPickerOption
                                    secondaryContent={{
                                        children: option.upn,
                                        className: styles.optionSecondaryContent,
                                    }}
                                    media={
                                        <Avatar
                                            shape="square"
                                            aria-hidden
                                            name={option.displayName}
                                            color="colorful" />
                                    }
                                    value={option.objectId}
                                    key={option.objectId}
                                    title={option.displayName}
                                    text={option.displayName}>
                                    <div className={styles.optionContent}>{option.displayName}</div>
                                </TagPickerOption>
                            ))
                        ) : (
                            <TagPickerOption value="no-options">
                                {t("pickerResultsNoItemsMessage")}
                            </TagPickerOption>
                        )}
                    </TagPickerList>
                </TagPicker>
            </Field>
        </div>
    );
}